import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ShoppingBag, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

function formatPrice(amount, currency, locale = "en") {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: (currency || "EUR").toUpperCase(),
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

export default function Marketplace() {
    const { t, i18n } = useTranslation();
    useMeta({
        title: "Marketplace de libros de segunda mano · RecomendaLibros",
        description: "Compra y vende libros de segunda mano entre lectores. Pagos seguros con escrow vía Stripe. Apoya la economía circular del libro.",
        canonical: "/marketplace",
    });
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const focusedId = params.get("id");
    const [items, setItems] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/marketplace/listings", {
                    params: { q: search || undefined, limit: 48 },
                });
                if (alive) setItems(data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
                if (alive) setItems([]);
            }
        })();
        return () => { alive = false; };
    }, [search]);

    const handleBuy = (listing) => {
        // Navigate to PDP instead of buying directly — user must accept T&Cs
        // and review the price breakdown before going to Stripe.
        navigate(`/marketplace/${listing.id}`);
    };

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t("marketplace.eyebrow")}
                </p>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <h1
                        className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight"
                        style={{ fontVariationSettings: '"wght" 700, "wdth" 75, "opsz" 144' }}
                    >
                        {t("marketplace.title")}
                    </h1>
                    <Link
                        to="/sell"
                        data-testid="marketplace-sell-cta"
                        className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2.5 text-sm uppercase tracking-widest"
                    >
                        {t("marketplace.nav_sell")}
                    </Link>
                </div>
                <p className="text-[var(--ink-muted)] mt-4 text-lg max-w-2xl">
                    {t("marketplace.lead")}
                </p>
                <p
                    data-testid="marketplace-social-note"
                    className="text-[var(--ink-dim)] mt-4 text-sm max-w-2xl italic border-l-2 border-[var(--accent)] pl-4"
                >
                    {t("marketplace.social_commission_note")}
                </p>
            </div>

            <div className="flex items-center gap-3 border-b border-[var(--border-strong)] pb-3 mb-10 max-w-md">
                <Search size={16} strokeWidth={1.5} className="text-[var(--ink-dim)]" />
                <input
                    data-testid="marketplace-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("marketplace.search_placeholder")}
                    className="flex-1 bg-transparent border-0 outline-none text-base text-[var(--ink)] py-1"
                />
            </div>

            {items === null ? (
                <p className="font-display text-2xl text-[var(--ink)] animate-slow-pulse italic">…</p>
            ) : items.length === 0 ? (
                <div data-testid="marketplace-empty" className="text-center py-24 border border-[var(--border-strong)] bg-[var(--bg-elevated)]">
                    <ShoppingBag size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                    <p className="font-display text-3xl text-[var(--ink)] mb-3"
                       style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                        {t("marketplace.empty_title")}
                    </p>
                    <p className="text-[var(--ink-muted)] mb-8 max-w-md mx-auto">
                        {t("marketplace.empty_lead")}
                    </p>
                    <Link to="/sell" className="inline-block bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-8 py-3 text-sm uppercase tracking-widest">
                        {t("marketplace.empty_cta")}
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                    {items.map((it, i) => {
                        const isFocused = it.id === focusedId;
                        return (
                            <article
                                key={it.id}
                                data-testid={`listing-${i}`}
                                className={`flex flex-col bg-[var(--bg-elevated)] border p-5 transition-all hover:-translate-y-1 ${
                                    isFocused ? "border-[var(--accent)]" : "border-[var(--border)]"
                                }`}
                            >
                                <div className="aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border)] mb-4 overflow-hidden flex items-center justify-center">
                                    {it.cover_url ? (
                                        <img src={it.cover_url} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                                    ) : (
                                        <ShoppingBag size={24} strokeWidth={1} className="text-[var(--accent)]" />
                                    )}
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] mb-1">
                                    {t(`marketplace.condition_${it.condition}`)}
                                </p>
                                <h3 className="font-display text-xl text-[var(--ink)] leading-tight mb-1 line-clamp-2"
                                    style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 24' }}>
                                    {it.title}
                                </h3>
                                <p className="text-sm text-[var(--ink-muted)] mb-3">{it.author}</p>
                                {it.description && (
                                    <p className="text-xs text-[var(--ink-dim)] line-clamp-2 mb-3">{it.description}</p>
                                )}
                                {it.seller_name && (
                                    <p className="text-xs text-[var(--ink-dim)] mb-3">
                                        {t("marketplace.by_seller", { seller: it.seller_name })}
                                    </p>
                                )}
                                <div className="mt-auto pt-3 flex items-center justify-between gap-3 border-t border-[var(--border)]">
                                    <p className="font-display text-2xl text-[var(--ink)] font-mono-num"
                                       style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                                        {formatPrice(it.price, it.currency, i18n.language)}
                                    </p>
                                    <button
                                        type="button"
                                        data-testid={`buy-btn-${i}`}
                                        onClick={() => handleBuy(it)}
                                        className="inline-flex items-center gap-1.5 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-4 py-2 text-xs uppercase tracking-widest"
                                    >
                                        <ShoppingBag size={12} strokeWidth={1.5} />
                                        <span>{t("marketplace.see_details")}</span>
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </main>
    );
}
